<script>
import useVuelidate from '@vuelidate/core';
import {required} from '@vuelidate/validators';

/* COMPOSANTS */
import AkInputText from '@components/v2/input/AkInputText';
import AkInputTextArea from '@components/v2/input/AkInputTextArea.vue';
import AkDialog from '@components/general/AkDialog';
import GaView from '@components/v2/layout/GaView';
import AkInputSwitch from '@components/v2/input/AkInputSwitch.vue';
import AkButtonAction from '@components/v2/general/AkButtonAction.vue';
import GaListView from '@components/v2/layout/GaListView.vue';
import AkDatatablePagination from '@components/v2/general/AkDatatablePagination.vue';
import {Icon} from '@iconify/vue';

/* MIXINS */
import randomRef from '@mixins/randomRef';
import utilsMixin from '@mixins/utilsMixin';
import messageMixin from '@mixins/messageMixin';

/* SERVICES */
import messageTemplateService from '@services/messageTemplateService';

export default {
  components: {
    Icon,
    AkDatatablePagination,
    GaListView,
    AkButtonAction,
    AkInputSwitch,
    GaView,
    AkInputText,
    AkInputTextArea,
    AkDialog,
  },
  mixins: [randomRef, utilsMixin, messageMixin],
  setup() {
    return {v$: useVuelidate()};
  },
  head() {
    return {
      title: 'settings.message_template',
    };
  },
  data() {
    return {
      loading: true,
      list: [],
      item: {
        label: '',
        content: '',
      },
    };
  },
  validations() {
    return {
      item: {
        label: {required},
        content: {required},
      },
    };
  },
  mounted() {
    this.initMessageTemplateList();
  },
  computed: {
    templatePlaceholders() {
      const wrap = (name) => ['{', '{', name, '}', '}'].join('');
      return [
        {token: wrap('guestFirstName'), hint: this.$t('settings.message_template_placeholder_guest_first_name')},
        {token: wrap('checkinDate'), hint: this.$t('settings.message_template_placeholder_checkin_date')},
        {token: wrap('checkoutDate'), hint: this.$t('settings.message_template_placeholder_checkout_date')},
        {
          token: wrap('reservationLocalizer'),
          hint: this.$t('settings.message_template_placeholder_reservation_localizer'),
        },
        {token: wrap('nbBed'), hint: this.$t('settings.message_template_placeholder_nb_bed')},
        {token: wrap('nbBedroom'), hint: this.$t('settings.message_template_placeholder_nb_bedroom')},
        {token: wrap('nbBathroom'), hint: this.$t('settings.message_template_placeholder_nb_bathroom')},
        {token: wrap('nbNights'), hint: this.$t('settings.message_template_placeholder_nb_nights')},
        {token: wrap('nbAdults'), hint: this.$t('settings.message_template_placeholder_nb_adults')},
        {token: wrap('nbChildren'), hint: this.$t('settings.message_template_placeholder_nb_children')},
        {token: wrap('accommodationName'), hint: this.$t('settings.message_template_placeholder_accommodation_name')},
        {token: wrap('boardingPass'), hint: this.$t('settings.message_template_placeholder_boarding_pass')},
        {token: wrap('housekeeperName'), hint: this.$t('settings.message_template_placeholder_housekeeper_name')},
        {token: wrap('housekeeperPhone'), hint: this.$t('settings.message_template_placeholder_housekeeper_phone')},
        {token: wrap('ownerName'), hint: this.$t('settings.message_template_placeholder_owner_name')},
        {token: wrap('ownerPhone'), hint: this.$t('settings.message_template_placeholder_owner_phone')},
        {token: wrap('coffeeMachine'), hint: this.$t('settings.message_template_placeholder_coffee_machine')},
        {token: wrap('linenIncluded'), hint: this.$t('settings.message_template_placeholder_linen_included')},
        {token: wrap('wifiName'), hint: this.$t('settings.message_template_placeholder_wifi_name')},
        {token: wrap('wifiPassword'), hint: this.$t('settings.message_template_placeholder_wifi_password')},
        {token: wrap('floor'), hint: this.$t('settings.message_template_placeholder_floor')},
        {token: wrap('gardenClosed'), hint: this.$t('settings.message_template_placeholder_garden_closed')},
      ];
    },
  },
  methods: {
    initMessageTemplateList() {
      this.loading = true;
      messageTemplateService.messageTemplates().then((data) => {
        this.list = data;
        this.loading = false;
      });
    },
    create() {
      this.v$.$touch();
      if (this.v$.item.$error) {
        this.$refs.dialogCreate.init();
        return;
      }

      messageTemplateService
        .create(this.item)
        .then(() => {
          this.success(this.$t('settings.message_template_added'));
        })
        .catch((e) => {
          this.addError(this.msgForErr(this.$t, e));
        })
        .finally(() => {
          this.$refs.dialogCreate.hide();
          this.initMessageTemplateList();
        });
    },
    update() {
      this.v$.$touch();
      if (this.v$.item.$error) {
        this.$refs.dialogEdit.init();
        return;
      }

      messageTemplateService
        .update(this.item)
        .then(() => {
          this.success(this.$t('settings.message_template_updated'));
        })
        .catch((e) => {
          this.addError(this.msgForErr(this.$t, e));
        })
        .finally(() => {
          this.$refs.dialogEdit.hide();
          this.initMessageTemplateList();
        });
    },
    delete() {
      messageTemplateService
        .delete(this.item)
        .then(() => {
          this.success(this.$t('settings.message_template_deleted'));
        })
        .catch((e) => {
          this.addError(this.msgForErr(this.$t, e));
        })
        .finally(() => {
          this.$refs.dialogDelete.hide();
          this.initMessageTemplateList();
        });
    },
    openCreateDialog() {
      this.item = {label: '', content: '', active: true};
      this.$refs.dialogCreate.show();
    },
    openEditDialog(item) {
      this.item = {...item, active: item.active !== false};
      this.$refs.dialogEdit.show();
    },
    openDeleteDialog(item) {
      this.item = item;
      this.$refs.dialogDelete.show();
    },
  },
};
</script>

<template>
  <GaView :ref="ref" :title="$t('settings.message_template')">
    <template #action>
      <AkButtonAction :label="$t('settings.add_message_template')" little @click="openCreateDialog()"></AkButtonAction>
    </template>

    <template #content>
      <GaListView>
        <template #content>
          <AkDatatablePagination :loading="loading" :value="list" :lazy="false" dataKey="id">
            <template #empty>
              {{ $t('settings.message_template_empty') }}
            </template>
            <Column :header="$t('item_label.label')" field="label">
              <template #body="slotProps">
                <span>{{ slotProps.data.label }}</span>
              </template>
            </Column>
            <Column :header="$t('status')" field="active" header-style="width: 110px">
              <template #body="slotProps">
                <span :class="slotProps.data.active === false ? 'text-grey-500' : 'text-grey-900'">
                  {{ slotProps.data.active === false ? $t('disabled') : $t('enabled') }}
                </span>
              </template>
            </Column>
            <Column body-style="text-align: right; overflow: visible" header-style="width: 100px; text-align: btn">
              <template #body="slotProps">
                <div class="flex justify-end gap-2">
                  <span
                    class="md:hover:cursor-pointer"
                    @click="openEditDialog(slotProps.data)"
                    :title="$t('settings.update_message_template')">
                    <i class="ga-icon ga-edit text-grey-900 text-2xl" />
                  </span>
                  <span
                    class="md:hover:cursor-pointer"
                    @click="openDeleteDialog(slotProps.data)"
                    :title="$t('settings.delete_message_template')">
                    <i class="ga-icon ga-trash text-red text-2xl" />
                  </span>
                </div>
              </template>
            </Column>
          </AkDatatablePagination>
        </template>
      </GaListView>
    </template>

    <template #extra>
      <AkDialog
        ref="dialogCreate"
        :title="$t('settings.message_template')"
        :validate-label="$t('add')"
        width="450px"
        @validate="create()">
        <div class="flex flex-col gap-2">
          <AkInputText
            v-model="item.label"
            :label="$t('item_label.label')"
            :validator="v$.item.label"
            class-name="col-md-12" />
          <AkInputTextArea
            v-model="item.content"
            :label="$t('settings.message_template_content')"
            :validator="v$.item.content"
            :rows="6"
            class-name="col-md-12" />
          <div class="text-sm text-grey-500">
            <div>{{ $t('settings.message_template_placeholders_hint') }}</div>
            <ul class="list-disc pl-4">
              <li v-for="placeholder in templatePlaceholders" :key="placeholder.token">
                <code>{{ placeholder.token }}</code>
                : {{ placeholder.hint }}
              </li>
            </ul>
          </div>
        </div>
      </AkDialog>

      <AkDialog
        ref="dialogEdit"
        :title="$t('settings.message_template')"
        :validate-label="$t('update')"
        width="450px"
        @validate="update()">
        <div class="flex flex-col gap-2">
          <AkInputText
            v-model="item.label"
            :label="$t('item_label.label')"
            :validator="v$.item.label"
            class-name="col-md-12" />
          <AkInputTextArea
            v-model="item.content"
            :label="$t('settings.message_template_content')"
            :validator="v$.item.content"
            :rows="6"
            class-name="col-md-12" />
          <div class="text-sm text-grey-500">
            <div>{{ $t('settings.message_template_placeholders_hint') }}</div>
            <ul class="list-disc pl-4">
              <li v-for="placeholder in templatePlaceholders" :key="placeholder.token">
                <code>{{ placeholder.token }}</code>
                : {{ placeholder.hint }}
              </li>
            </ul>
          </div>
          <AkInputSwitch v-model="item.active" :label="$t('enabled')" :labelBefore="false" inline />
        </div>
      </AkDialog>

      <AkDialog
        ref="dialogDelete"
        :cancel-label="$t('no')"
        :title="$t('delete_dialog')"
        :validate-label="$t('yes')"
        width="450px"
        @validate="this.delete()">
        <div class="flex items-center gap-2">
          <Icon icon="fluent:warning-20-regular" class="text-red" width="26" />
          <span class="text-grey-900 font-normal">{{ $t('settings.confirm_delete_message_template') }}</span>
        </div>
      </AkDialog>
    </template>
  </GaView>
</template>
